import React, { useState, useRef, useEffect } from "react";
import {
  Modal,
  Spin,
  Row,
  Col,
  Card,
  Tabs,
  message,
  Button,
  Drawer,
  Space,
  Tooltip,
} from "antd";
import {
  RiShapesLine,
  RiShapeLine,
  RiArrowGoBackLine,
  RiCloseLine,
  RiCheckLine,
  RiAlarmWarningLine,
} from "react-icons/ri";
import {
  IndoorPositioning,
  IndoorPositioningPersonnel,
  IndoorPositioningStations,
  IndoorPositioningSymbols,
} from "../../../api";
import { useFloorPlanData } from "./hooks/useFloorPlanData";
import { useStations } from "./hooks/useStations";
import { useSymbols } from "./hooks/useSymbols";
import { useStationFilters } from "./hooks/useStationFilters";
import { useCoordinateConversion } from "./hooks/useCoordinateConversion";
import { useCanvasSize } from "./hooks/useCanvasSize";
import { useEditorMode } from "./hooks/useEditorMode";
import { useAreaPolygon } from "./hooks/useAreaPolygon";
import { useTripwires } from "./hooks/useTripwires";
import { useEditorLayers, editorLayerToggles } from "./hooks/useEditorLayers";
import {
  useTripwireAddMode,
  getTripwireAddHint,
} from "./hooks/useTripwireAddMode";
import FloorPlanConfiguration from "./components/FloorPlanConfiguration";
import { geoPayload, useGeoForm } from "./components/geoForm";
import {
  sizeChanged,
  sizeConfirmText,
  outsideCounts,
  outsideWarning,
  areaOutside,
} from "./utils/sizeChange";
import StationList from "./components/StationList";
import SymbolPalette from "./components/SymbolPalette";
import PlaneCanvas from "./components/Canvas/PlaneCanvas";
import { EditModeTitle } from "./components/Canvas/EditorViewport";
import LineCanvas from "./components/Canvas/LineCanvas";
import TunnelCanvas from "./components/Canvas/TunnelCanvas";
import TripwireOverlay from "./components/Canvas/TripwireOverlay";
import TripwirePopover from "./components/TripwirePopover";
import TripwireSettingsModal from "./components/TripwireSettingsModal";
import BatchPlacementModal from "./components/BatchPlacementModal";
import ReorderModal from "./components/ReorderModal";
import {
  CANVAS_CARD_BODY_HEIGHT,
  MIN_DIMENSION,
  STATION_HALF_SIZE,
} from "./utils/constants";
import { SYMBOL_HALF_SIZE, getSymbolById } from "./utils/symbolTypes";
import {
  calculateGridPositions,
  calculateLinePositions,
  calculateEvenDistribution,
  calculateFixedSpacing,
  validatePositions,
  calculateTunnelPositions,
  validateTunnelPositions,
} from "./utils/batchPlacement";
import {
  adjustStationsForDimensionChange,
  laneYMeters,
} from "./utils/tunnelUtils";
import { isSelfIntersecting, clampPoint } from "./utils/polygonUtils";
import {
  buildLaneSegment,
  buildTripwirePayload,
  buildFlipPayload,
  popoverAnchorForLine,
  isProvisionalTripwireId,
} from "./utils/tripwireUtils";

const FloorPlanModal = ({ open, onClose, machineID }) => {
  const canvasContainerRef = useRef(null);
  const canvasRef = useRef(null);
  const previousDimensionsRef = useRef(null);
  const [personnel, setPersonnel] = useState([]);

  const floorPlan = useFloorPlanData(machineID, open);
  const {
    dimensions,
    setDimensions,
    savedDimensions,
    setSavedDimensions,
    layoutType,
    setLayoutType,
    imageUrl,
    boundaryArea,
    loading,
    uploading,
    handleUpload,
    handleRemoveImage,
  } = floorPlan;
  const [geoForm, setGeoForm] = useGeoForm(floorPlan, open);

  const canvasSize = useCanvasSize(
    dimensions,
    open,
    canvasContainerRef,
    layoutType,
    { fillPlane: true },
  );
  const {
    stations,
    placedStations,
    setPlacedStations,
    stationsLoading,
    loadStations,
    updateStationInState,
  } = useStations(machineID, open);

  const { symbols, placedSymbols, setPlacedSymbols, symbolsLoading } =
    useSymbols(machineID, open);

  const areaPolygon = useAreaPolygon(boundaryArea, open);
  // Hook-based antd message instance (message.useMessage()), not the static
  // `message` import above -- see useTripwires.js's header comment (BUG2:
  // the static singleton is unsupported/unreliable under this app's React 17
  // legacy ReactDOM.render root). `contextHolder` below is rendered near the
  // top of this component's returned tree so it stays mounted for as long as
  // FloorPlanModal itself is mounted -- covering the settings modal being
  // open and any tripwire drag, not just while the outer Modal is `open`.
  // Only the tripwire hook/modal switch to this -- every other `message.xxx`
  // call in this file keeps using the static import unchanged.
  const [tripwireMessage, tripwireMessageContextHolder] = message.useMessage();
  const tripwiresState = useTripwires(machineID, open, tripwireMessage);
  const tripwireAddMode = useTripwireAddMode();

  const {
    nameFilter,
    setNameFilter,
    placementFilter,
    setPlacementFilter,
    categoryFilter,
    setCategoryFilter,
    filteredStations,
    getUniqueCategories,
    isStationPlaced,
  } = useStationFilters(stations, placedStations);

  const [selectedStationId, setSelectedStationId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [batchMode, setBatchMode] = useState(false);
  const [selectedStationsOrder, setSelectedStationsOrder] = useState(new Map());
  const [batchPlacementModalOpen, setBatchPlacementModalOpen] = useState(false);
  const [reorderModalOpen, setReorderModalOpen] = useState(false);
  const [selectedSymbolType, setSelectedSymbolType] = useState(null);
  const [symbolPlacingMode, setSymbolPlacingMode] = useState("single");
  const [symbolDrawerOpen, setSymbolDrawerOpen] = useState(false);
  const [areaEditMode, setAreaEditMode] = useState(false);
  // 圖層's 顯示 rows: view-only, reset on every open.
  const overlays = useEditorLayers(open);
  // Level-1 focus: which tripwire shows its endpoint handles + meta popover.
  // Distinct from the add-mode's own "which points has the operator placed
  // so far" state (tripwireAddMode.points) -- a line is either being FOCUSED
  // (an existing line, level 1) or being PLACED (a brand new line, add
  // mode); the two never overlap, and add-mode entry always clears focus.
  const [focusedTripwireId, setFocusedTripwireId] = useState(null);
  const [tripwireModalOpen, setTripwireModalOpen] = useState(false);
  const [tripwireDraft, setTripwireDraft] = useState(null);
  // A midpoint/edge-anchored popover can sit on top of an endpoint handle on
  // a short/slanted line and make it untouchable mid-drag -- hide the
  // level-1 popover for the duration of ANY drag-capable pointerdown
  // (TripwireOverlay's onDragActiveChange fires on handle AND body
  // pointerdown, even for what turns out to be a plain click) and let it
  // reappear on drop, at the (possibly new, optimistically-updated) anchor.
  const [tripwireDragActive, setTripwireDragActive] = useState(false);

  // 瀏覽 / 編輯模式 and the zoom/pan view (plane only). Leaving 編輯模式
  // cancels whichever sub-mode is active; it saves and discards nothing.
  const editor = useEditorMode({
    open,
    machineID,
    // The map under the editor follows the form (套用 shows the new alignment
    // before 保存), not the values loaded when the editor opened.
    floorPlan: {
      ...floorPlan,
      geoEnabled: geoForm.enabled,
      geoOriginLat: geoForm.lat,
      geoOriginLng: geoForm.lng,
      geoRotationDeg: geoForm.rotationDeg,
    },
    canvasSize,
    onLeave: () => {
      setAreaEditMode(false);
      tripwireAddMode.cancel();
      setBatchMode(false);
      setSelectedStationsOrder(new Map());
      setSelectedSymbolType(null);
      setSymbolPlacingMode("single");
      setFocusedTripwireId(null);
    },
  });
  // One viewport per layout (hidden Tabs panes stay mounted, so each canvas
  // projects with its own); the active layout's drives the pointer handlers.
  // The plane one carries the editor's zoom/pan.
  const viewports = useCoordinateConversion(dimensions, canvasSize, editor.view);
  const viewport = viewports[layoutType] ?? viewports.plane;

  useEffect(() => {
    if (!open) {
      setSymbolDrawerOpen(false);
      setSelectedSymbolType(null);
      setSymbolPlacingMode("single");
    }
  }, [open]);

  useEffect(() => {
    if (!open || !machineID) return;
    IndoorPositioningPersonnel.GetPersonnel(machineID).then((res) => {
      if (res.code === 200 && Array.isArray(res.data)) {
        setPersonnel(res.data);
      }
    });
  }, [open, machineID]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (selectedSymbolType) {
          e.preventDefault();
          e.stopPropagation();
          setSelectedSymbolType(null);
          setSymbolPlacingMode("single");
        } else if (symbolDrawerOpen) {
          setSymbolDrawerOpen(false);
        }
      }
    };

    if (symbolDrawerOpen || selectedSymbolType) {
      window.addEventListener("keydown", handleKeyDown, true);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [selectedSymbolType, symbolDrawerOpen]);

  // Esc for the 越線 tool: while adding, cancel the in-progress placement;
  // otherwise, if a line is focused (level 1), unfocus it. Mirrors the
  // symbol-tool Esc handler above (its own effect, since the two tools'
  // active conditions are independent and each should only attach a
  // listener while it actually has something to cancel).
  useEffect(() => {
    const handleTripwireKeyDown = (e) => {
      if (e.key !== "Escape") return;
      if (tripwireAddMode.active) {
        e.preventDefault();
        e.stopPropagation();
        tripwireAddMode.cancel();
      } else if (focusedTripwireId != null) {
        setFocusedTripwireId(null);
      }
    };

    if (tripwireAddMode.active || focusedTripwireId != null) {
      window.addEventListener("keydown", handleTripwireKeyDown, true);
    }

    return () => {
      window.removeEventListener("keydown", handleTripwireKeyDown, true);
    };
  }, [tripwireAddMode.active, tripwireAddMode.cancel, focusedTripwireId]);

  useEffect(() => {
    if (layoutType !== "tunnel" || placedStations.length === 0) {
      previousDimensionsRef.current = dimensions;
      return;
    }

    const prevDimensions = previousDimensionsRef.current;

    if (
      prevDimensions &&
      prevDimensions.height !== dimensions.height &&
      prevDimensions.height > 0
    ) {
      const adjustedStations = adjustStationsForDimensionChange(
        placedStations,
        prevDimensions.height,
        dimensions.height,
      );

      setPlacedStations(adjustedStations);

      adjustedStations.forEach(async (station) => {
        if (station.position_meters) {
          try {
            await IndoorPositioningStations.UpdateStation(station.id, {
              position_x_meters: station.position_meters.x,
              position_y_meters: station.position_meters.y,
            });
          } catch (error) {
            console.error(`更新基站 ${station.id} 位置失敗:`, error);
          }
        }
      });
    }

    previousDimensionsRef.current = dimensions;
  }, [dimensions, layoutType, placedStations]);

  const handleStationClick = async (station) => {
    if (!station) return;
    overlays.show("stations");
    // 瀏覽: the click only switches to 編輯模式; the next one places.
    if (!editor.editing) {
      editor.enterEditMode();
      return;
    }

    const isPlaced = isStationPlaced(station);
    if (isPlaced) {
      return;
    }

    const initialPositionMeters = {
      x: dimensions.width / 2,
      y:
        layoutType === "tunnel"
          ? laneYMeters("upper", dimensions.height)
          : dimensions.height / 2,
    };

    setPlacedStations((prev) => {
      return [
        ...prev,
        {
          ...station,
          position_meters: initialPositionMeters,
        },
      ];
    });

    try {
      await IndoorPositioningStations.UpdateStation(station.id, {
        position_x_meters: initialPositionMeters.x,
        position_y_meters: initialPositionMeters.y,
      });
      message.success(`已添加 ${station.name || "基站"} 到畫布`);
    } catch (error) {
      console.error("保存基站位置失敗:", error);
      message.error("保存基站位置失敗");
      setPlacedStations((prev) => prev.filter((s) => s.id !== station.id));
    }
  };

  const handleRemoveFromCanvas = async (station) => {
    if (!station?.id) return;

    try {
      await IndoorPositioningStations.UpdateStation(station.id, {
        position_x_meters: null,
        position_y_meters: null,
      });

      setPlacedStations((prev) => prev.filter((s) => s.id !== station.id));

      message.success(`已從畫布移除 ${station.name || "基站"}`);
    } catch (error) {
      console.error("移除基站失敗:", error);
      message.error("移除基站失敗");
    }
  };

  const handleUpdateRadius = async (stationId, radiusMeters) => {
    try {
      await IndoorPositioningStations.UpdateStation(stationId, {
        radius_meters: radiusMeters,
      });
      updateStationInState(stationId, { radius_meters: radiusMeters });
      message.success("已更新防護區半徑");
    } catch (error) {
      console.error("更新防護區半徑失敗:", error);
      message.error("更新防護區半徑失敗");
      throw error;
    }
  };

  const handleUpdateAllowedPersonnel = async (stationId, allowedIds) => {
    try {
      await IndoorPositioningStations.UpdateStation(stationId, {
        allowed_personnel_ids: allowedIds,
      });
      updateStationInState(stationId, { allowed_personnel_ids: allowedIds });
      message.success("已更新防護區人員白名單");
    } catch (error) {
      console.error("更新防護區人員白名單失敗:", error);
      message.error("更新防護區人員白名單失敗");
      throw error;
    }
  };

  const handleDragStop = async (stationId, data) => {
    let updatedStation = null;

    // react-draggable reports the glyph's top-left; the projection works in true
    // canvas positions, so add the half-glyph the renderer subtracted.
    const positionPixels = {
      x: data.x + STATION_HALF_SIZE,
      y: data.y + STATION_HALF_SIZE,
    };
    const positionMeters = viewport.unproject(positionPixels);

    setPlacedStations((prev) =>
      prev.map((station) => {
        if (station.id === stationId) {
          updatedStation = {
            ...station,
            position_meters: positionMeters,
          };
          return updatedStation;
        }
        return station;
      }),
    );

    if (updatedStation) {
      try {
        await IndoorPositioningStations.UpdateStation(stationId, {
          position_x_meters: positionMeters.x,
          position_y_meters: positionMeters.y,
        });
      } catch (error) {
        console.error("保存基站位置失敗:", error);
        message.error("保存基站位置失敗");
      }
    }
  };

  const handleLineDragStop = async (stationId, data) => {
    let updatedStation = null;

    const position_meters = viewports.line.pick({
      x: data.x + STATION_HALF_SIZE,
      y: data.y + STATION_HALF_SIZE,
    });

    setPlacedStations((prev) =>
      prev.map((station) => {
        if (station.id === stationId) {
          updatedStation = {
            ...station,
            position_meters,
          };
          return updatedStation;
        }
        return station;
      }),
    );

    if (updatedStation) {
      try {
        await IndoorPositioningStations.UpdateStation(stationId, {
          position_x_meters: updatedStation.position_meters.x,
          position_y_meters: updatedStation.position_meters.y,
        });
      } catch (error) {
        console.error("保存基站位置失敗:", error);
        message.error("保存基站位置失敗");
      }
    }
  };

  const handleTunnelDragStop = async (stationId, data) => {
    let updatedStation = null;

    const position_meters = viewports.tunnel.pick({
      x: data.x + STATION_HALF_SIZE,
      y: data.y + STATION_HALF_SIZE,
    });

    setPlacedStations((prev) =>
      prev.map((station) => {
        if (station.id === stationId) {
          updatedStation = {
            ...station,
            position_meters,
          };
          return updatedStation;
        }
        return station;
      }),
    );

    if (updatedStation) {
      try {
        await IndoorPositioningStations.UpdateStation(stationId, {
          position_x_meters: updatedStation.position_meters.x,
          position_y_meters: updatedStation.position_meters.y,
        });
      } catch (error) {
        console.error("保存基站位置失敗:", error);
        message.error("保存基站位置失敗");
      }
    }
  };

  const handleSave = async () => {
    if (!machineID) {
      message.error("機器ID不存在");
      return;
    }

    if (dimensions.width < MIN_DIMENSION || dimensions.height < MIN_DIMENSION) {
      message.error(`平面圖尺寸最小為 ${MIN_DIMENSION}m × ${MIN_DIMENSION}m`);
      return;
    }

    const boundaryVerts = areaPolygon.vertices;
    if (
      layoutType === "plane" &&
      boundaryVerts.length >= 3 &&
      isSelfIntersecting(boundaryVerts)
    ) {
      message.error("可行區域邊界自相交，請修正後再保存");
      return;
    }

    // The backend 400s an area outside the floor: say so instead of sending.
    if (layoutType === "plane" && areaOutside(boundaryVerts, dimensions)) {
      message.error("可行區域超出新的場域尺寸，請先調整可行區域或尺寸再保存");
      return;
    }

    if (!sizeChanged(savedDimensions, dimensions)) {
      await persist();
      return;
    }
    // Items keep their metres; the confirmation only says what ends up outside.
    const warning = outsideWarning(
      outsideCounts(dimensions, {
        stations: placedStations,
        symbols: placedSymbols,
        areaVertices: layoutType === "plane" ? boundaryVerts : [],
        tripwires: tripwiresState.tripwires,
      }),
    );
    Modal.confirm({
      title: "場域尺寸",
      content: (
        <div>
          <p style={{ margin: 0 }}>
            {sizeConfirmText(savedDimensions, dimensions)}
          </p>
          {warning && (
            <p style={{ margin: "8px 0 0", color: "#ad6800" }}>{warning}</p>
          )}
        </div>
      ),
      okText: "保存",
      cancelText: "取消",
      onOk: persist,
    });
  };

  const persist = async () => {
    const boundaryVerts = areaPolygon.vertices;
    setSaving(true);
    try {
      const payload = {
        layout_type: layoutType,
        floor_width: dimensions.width,
        floor_height: dimensions.height,
        floor_image_url: imageUrl,
        ...geoPayload(geoForm, layoutType),
      };
      // Plane only: persist (or clear) the walkable area. Non-plane layouts omit
      // the key so any stored geometry is left untouched.
      if (layoutType === "plane") {
        payload.boundary_area =
          boundaryVerts.length >= 3 ? boundaryVerts : null;
      }
      const response = await IndoorPositioning.UpdateIndoorPositioning(
        machineID,
        payload,
      );

      if (response.code !== 200) {
        message.error(response.message || "保存失敗");
        setSaving(false);
        return;
      }

      const positionPromises = placedStations.map((station) => {
        if (!station.position_meters) {
          console.warn(`Station ${station.id} missing position_meters`);
          return Promise.resolve();
        }
        return IndoorPositioningStations.UpdateStation(station.id, {
          position_x_meters: station.position_meters.x,
          position_y_meters: station.position_meters.y,
        });
      });

      const placedStationIds = new Set(placedStations.map((s) => s.id));
      const stationsToClear = stations.filter(
        (s) =>
          s.canvas_x !== null &&
          s.canvas_y !== null &&
          !placedStationIds.has(s.id),
      );
      const clearPromises = stationsToClear.map((station) =>
        IndoorPositioningStations.UpdateStation(station.id, {
          position_x_meters: null,
          position_y_meters: null,
        }),
      );

      const symbolPromises = placedSymbols.map((symbol) => {
        if (!symbol.position_meters) {
          console.warn(`Symbol ${symbol.id} missing position_meters`);
          return Promise.resolve();
        }
        return IndoorPositioningSymbols.UpdateSymbol(symbol.id, {
          position_x_meters: symbol.position_meters.x,
          position_y_meters: symbol.position_meters.y,
        });
      });

      await Promise.all([
        ...positionPromises,
        ...clearPromises,
        ...symbolPromises,
      ]);

      // 保存 is what makes an alignment take effect: drop the 已對齊 note, then
      // reload so the editor shows what the server stored (switch, position,
      // size) instead of the values loaded when it opened.
      setGeoForm((g) => ({ ...g, aligned: false, cleared: false }));
      setSavedDimensions(dimensions);
      await floorPlan.loadFloorPlan();
      message.success("平面圖設定已保存");
    } catch (error) {
      console.error("保存平面圖設定失敗:", error);
      message.error("保存失敗");
    } finally {
      setSaving(false);
    }
  };

  const handleBatchModeToggle = () => {
    editor.enterEditMode();
    setBatchMode((prev) => !prev);
    setSelectedStationsOrder(new Map());
  };

  const handleSelectionChange = (stationId) => {
    setSelectedStationsOrder((prev) => {
      const newMap = new Map(prev);
      if (newMap.has(stationId)) {
        newMap.delete(stationId);
        const entries = Array.from(newMap.entries()).sort(
          (a, b) => a[1] - b[1],
        );
        return new Map(entries.map(([id], idx) => [id, idx + 1]));
      } else {
        newMap.set(stationId, newMap.size + 1);
      }
      return newMap;
    });
  };

  const handleSelectAll = () => {
    const allStations = filteredStations();
    setSelectedStationsOrder(
      new Map(allStations.map((s, idx) => [s.id, idx + 1])),
    );
  };

  const handleDeselectAll = () => {
    setSelectedStationsOrder(new Map());
  };

  const handleOpenBatchPlacement = () => {
    const unplacedStations = getSelectedUnplaced();
    if (unplacedStations.length === 0) {
      message.warning("請先選擇未放置的基站");
      return;
    }
    setBatchPlacementModalOpen(true);
  };

  const getOrderedSelectedStations = () => {
    const orderedEntries = Array.from(selectedStationsOrder.entries()).sort(
      (a, b) => a[1] - b[1],
    );
    return orderedEntries
      .map(([stationId]) => stations.find((s) => s.id === stationId))
      .filter(Boolean);
  };

  const getSelectedUnplaced = () => {
    return getOrderedSelectedStations().filter((s) => !isStationPlaced(s));
  };

  const getSelectedPlaced = () => {
    return getOrderedSelectedStations().filter((s) => isStationPlaced(s));
  };

  const handleOpenReorder = () => {
    const unplacedStations = getSelectedUnplaced();
    if (unplacedStations.length < 2) {
      message.warning("至少需要選擇2個未放置的基站才能排序");
      return;
    }
    setReorderModalOpen(true);
  };

  const handleReorder = (reorderedStations) => {
    const placedStations = getSelectedPlaced();
    const allReordered = [...reorderedStations, ...placedStations];
    const newOrder = new Map(
      allReordered.map((station, idx) => [station.id, idx + 1]),
    );
    setSelectedStationsOrder(newOrder);
    setReorderModalOpen(false);
    message.success("順序已更新");
  };

  const handleBatchRemove = () => {
    const placedStations = getSelectedPlaced();
    if (placedStations.length === 0) {
      message.warning("沒有已放置的基站被選中");
      return;
    }

    Modal.confirm({
      title: `確定要移除 ${placedStations.length} 個基站嗎？`,
      content: "移除後基站將從畫布上消失",
      okText: "確定",
      cancelText: "取消",
      okType: "danger",
      onOk: async () => {
        try {
          await Promise.all(
            placedStations.map((station) =>
              IndoorPositioningStations.UpdateStation(station.id, {
                position_x_meters: null,
                position_y_meters: null,
              }),
            ),
          );

          const removedIds = new Set(placedStations.map((s) => s.id));
          setPlacedStations((prev) =>
            prev.filter((s) => !removedIds.has(s.id)),
          );

          setSelectedStationsOrder((prev) => {
            const newMap = new Map(prev);
            removedIds.forEach((id) => newMap.delete(id));
            const entries = Array.from(newMap.entries()).sort(
              (a, b) => a[1] - b[1],
            );
            return new Map(entries.map(([id], idx) => [id, idx + 1]));
          });

          message.success(`已移除 ${placedStations.length} 個基站`);
        } catch (error) {
          console.error("批量移除失敗:", error);
          message.error("批量移除失敗");
        }
      },
    });
  };

  const handleBatchPlacement = async (pattern, params) => {
    const selectedStations = getOrderedSelectedStations().filter(
      (s) => !isStationPlaced(s),
    );

    if (selectedStations.length === 0) {
      message.warning("沒有可放置的基站");
      return;
    }

    let positions;
    try {
      if (layoutType === "plane") {
        if (pattern === "grid") {
          positions = calculateGridPositions(
            selectedStations.length,
            params.rows,
            params.cols,
            params.startX,
            params.startY,
            params.spacingX,
            params.spacingY,
          );
        } else {
          positions = calculateLinePositions(
            selectedStations.length,
            params.startX,
            params.startY,
            params.spacing,
            params.direction,
            params.angle,
          );
        }

        if (!validatePositions(positions, dimensions, false)) {
          message.error("部分基站超出畫布範圍，請調整參數");
          return;
        }
      } else if (layoutType === "line") {
        if (pattern === "even") {
          positions = calculateEvenDistribution(
            selectedStations.length,
            dimensions.width,
          );
        } else {
          positions = calculateFixedSpacing(
            selectedStations.length,
            params.startPosition,
            params.spacing,
          );
        }

        if (!validatePositions(positions, dimensions, true)) {
          message.error("部分基站超出線段範圍，請調整參數");
          return;
        }

        positions = positions.map((pos) => ({
          x: pos.x,
          y: dimensions.height / 2,
        }));
      } else if (layoutType === "tunnel") {
        positions = calculateTunnelPositions(
          selectedStations.length,
          dimensions.width,
          dimensions.height,
          pattern,
          params,
        );

        if (!validateTunnelPositions(positions, dimensions)) {
          message.error("部分基站超出隧道範圍，請調整參數");
          return;
        }
      }
    } catch (error) {
      console.error("計算位置失敗:", error);
      message.error("計算位置失敗");
      return;
    }

    const updates = selectedStations.map((station, idx) => ({
      station,
      position: positions[idx],
    }));

    const newPlacedStations = updates.map((u) => ({
      ...u.station,
      position_meters: u.position,
    }));

    setPlacedStations((prev) => [...prev, ...newPlacedStations]);
    overlays.show("stations");

    try {
      await Promise.all(
        updates.map((u) =>
          IndoorPositioningStations.UpdateStation(u.station.id, {
            position_x_meters: u.position.x,
            position_y_meters: u.position.y,
          }),
        ),
      );
      message.success(`已批量放置 ${updates.length} 個基站`);
      setSelectedStationsOrder(new Map());
      setBatchMode(false);
      setBatchPlacementModalOpen(false);
    } catch (error) {
      console.error("批量放置失敗:", error);
      message.error("批量放置失敗");
      const failedIds = new Set(updates.map((u) => u.station.id));
      setPlacedStations((prev) => prev.filter((s) => !failedIds.has(s.id)));
    }
  };

  // Canvas pixel -> metres through the active viewport, clamped to the floor.
  const pixelToClampedMeters = (px, py) => {
    const m = viewport.unproject({ x: px, y: py });
    return clampPoint(m.x, m.y, dimensions.width, dimensions.height);
  };

  const handleAreaCanvasClick = (e) => {
    if (!areaEditMode || !areaPolygon.drawing) return;
    const canvas = canvasRef.current;
    if (!canvas || !viewport.isValid) return;
    const rect = canvas.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    if (px < 0 || px > canvasSize.width || py < 0 || py > canvasSize.height) {
      return;
    }
    areaPolygon.addVertex(pixelToClampedMeters(px, py));
  };

  // 越線 (tripwire) add-only tool. Plane: two raw clicks define an arbitrary
  // segment. line/tunnel: one click picks a lane position; the segment spans
  // the full lane at that x (buildLaneSegment -- see utils/tripwireUtils.js
  // for why x is the lane axis and how that determines the A/B side). Both
  // end the same way: POST with backend defaults (name null, direction
  // both, applies_to both, enabled true), exit adding mode, and focus the
  // new line (level 1) -- the settings modal is never opened as part of
  // creation (2026-09-22(b) revision).
  //
  // Optimistic (product owner: "after the second point landed, the line
  // disappears for a second then appears after saved successfully"):
  // `tripwiresState.create`'s `onProvisional` callback fires synchronously
  // (before its POST even starts), handing back the provisional line's
  // negative temp id -- focus moves there immediately, same as focusing a
  // confirmed line always has. Once the POST resolves, if focus is STILL on
  // that temp id (the operator hasn't since clicked elsewhere / Esc'd), it
  // is carried over to the real server id on success, or cleared on failure
  // (the provisional line itself is already gone by then -- see
  // useTripwires.create).
  const handleTripwireCanvasClick = async (e) => {
    if (!tripwireAddMode.active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    if (px < 0 || px > canvasSize.width || py < 0 || py > canvasSize.height) {
      return;
    }

    if (layoutType === "plane") {
      if (!viewport.isValid) return;
      const point = pixelToClampedMeters(px, py);
      if (tripwireAddMode.points.length === 0) {
        tripwireAddMode.addPoint(point);
        return;
      }
      const points = [tripwireAddMode.points[0], point];
      tripwireAddMode.complete();
      let tempId;
      const created = await tripwiresState.create(
        buildTripwirePayload({ points }),
        {
          onProvisional: (id) => {
            tempId = id;
            setFocusedTripwireId(id);
          },
        },
      );
      setFocusedTripwireId((prev) => {
        if (prev !== tempId) return prev;
        return created ? created.id : null;
      });
      return;
    }

    const xMeters = Math.min(
      Math.max((px / (canvasSize.width || 1)) * (dimensions.width || 1), 0),
      dimensions.width,
    );
    tripwireAddMode.complete();
    let tempId;
    const created = await tripwiresState.create(
      buildTripwirePayload({ points: buildLaneSegment(xMeters, dimensions) }),
      {
        onProvisional: (id) => {
          tempId = id;
          setFocusedTripwireId(id);
        },
      },
    );
    setFocusedTripwireId((prev) => {
      if (prev !== tempId) return prev;
      return created ? created.id : null;
    });
  };

  // Plane-only rubber-band preview: after point 1, the pending second
  // endpoint follows the mouse until the operator clicks (or cancels).
  const handleTripwireCanvasMouseMove = (e) => {
    if (
      !tripwireAddMode.active ||
      tripwireAddMode.points.length !== 1 ||
      layoutType !== "plane" ||
      !viewport.isValid
    ) {
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    tripwireAddMode.setPreviewPoint(pixelToClampedMeters(px, py));
  };

  // Level 0 -> level 1: a click (not a drag) on a line's body focuses it.
  const handleTripwireFocus = (id) => {
    setFocusedTripwireId(id);
  };

  const handleTripwireBackgroundClick = () => {
    setFocusedTripwireId(null);
  };

  // Level 1 -> level 2: the popover's 編輯 button opens the settings modal
  // for whichever line is currently focused. Blocked while the focused line
  // is still PROVISIONAL (its create POST hasn't resolved) -- there is no
  // server id yet for the modal's Save/Delete/Flip actions to target, and
  // TripwirePopover already disables the 編輯 button for that case; this is
  // the same defense-in-depth as useTripwires.update/remove's own guard.
  const handleTripwireEditOpen = () => {
    if (isProvisionalTripwireId(focusedTripwireId)) return;
    const tw = tripwiresState.tripwires.find((t) => t.id === focusedTripwireId);
    if (!tw) return;
    setTripwireDraft(tw);
    setTripwireModalOpen(true);
  };

  const handleTripwireEndpointDragEnd = (id, index, point) => {
    const tw = tripwiresState.tripwires.find((t) => t.id === id);
    if (!tw) return;
    const points = tw.points.map((p, i) => (i === index ? point : p));
    tripwiresState.update(id, { points });
  };

  // Level 0: drag the whole line (both endpoints translated together, or
  // the gate slid along the lane axis). PUT on drop; on failure the hook
  // never updates local state, so the line reverts to its last-known-good
  // position for free (see useTripwires.update / TripwireOverlay's header
  // comment).
  const handleTripwireBodyDragEnd = (id, points) => {
    tripwiresState.update(id, { points });
  };

  const handleTripwireLaneDragEnd = (id, xMeters) => {
    const points = buildLaneSegment(xMeters, dimensions);
    tripwiresState.update(id, { points });
  };

  const handleTripwireSave = async (payload) => {
    if (tripwireDraft?.id) {
      return tripwiresState.update(tripwireDraft.id, payload);
    }
    return tripwiresState.create(payload);
  };

  const handleTripwireDelete = async (id) => {
    const ok = await tripwiresState.remove(id);
    if (ok) {
      setTripwireModalOpen(false);
      setFocusedTripwireId(null);
    }
    return ok;
  };

  // 「翻轉限制側」 (single-direction lines only): swap the two points via
  // the SAME optimistic PUT path as every other drag/edit -- geometry (the
  // physical segment) is unchanged, only which end is "first" (and so which
  // side is the restricted one) flips. Lane layouts reuse this unchanged:
  // buildFlipPayload works on any 2-point segment, including a gate's
  // `[[x,0],[x,spanM]]`. If the settings modal has this same line open
  // (tripwireDraft), refresh its local snapshot too so a subsequent Save
  // from the modal does not resurrect the pre-flip points.
  const handleTripwireFlip = async (id, points) => {
    const updated = await tripwiresState.update(id, buildFlipPayload(points));
    if (updated && tripwireDraft?.id === id) {
      setTripwireDraft(updated);
    }
    return updated;
  };

  const closeTripwireModal = () => {
    setTripwireModalOpen(false);
    setTripwireDraft(null);
  };

  const toggleTripwireAddMode = () => {
    editor.enterEditMode();
    if (tripwireAddMode.active) {
      tripwireAddMode.cancel();
      return;
    }
    setSelectedSymbolType(null);
    setSymbolPlacingMode("single");
    setAreaEditMode(false);
    setFocusedTripwireId(null);
    overlays.show("tripwires");
    tripwireAddMode.start();
  };

  const handleVertexClick = (index) => {
    if (
      areaPolygon.drawing &&
      index === 0 &&
      areaPolygon.vertices.length >= 3
    ) {
      areaPolygon.finish();
    }
  };

  const toggleAreaEditMode = () => {
    editor.enterEditMode();
    if (!areaEditMode) overlays.show("area");
    setAreaEditMode((prev) => {
      const next = !prev;
      if (next) {
        setSelectedSymbolType(null);
        setSymbolPlacingMode("single");
        tripwireAddMode.cancel();
        setFocusedTripwireId(null);
      }
      return next;
    });
  };

  // Check button: commit the area (close an open ring if it has enough points)
  // and collapse the sub-menu. Persisting to the backend still happens via the
  // modal's 保存 button, consistent with station/symbol edits.
  const handleFinishAreaEdit = () => {
    if (areaPolygon.drawing && areaPolygon.vertices.length >= 3) {
      areaPolygon.finish();
    }
    setAreaEditMode(false);
  };

  const handleClearArea = () => {
    Modal.confirm({
      title: "清除可行區域",
      content: "確定要清除目前的可行區域邊界嗎？此操作無法復原。",
      okText: "確定",
      cancelText: "取消",
      okType: "danger",
      onOk: () => areaPolygon.clear(),
    });
  };

  const handleSymbolSelect = (symbolType) => {
    editor.enterEditMode();
    overlays.show("symbols");
    setAreaEditMode(false);
    tripwireAddMode.cancel();
    setFocusedTripwireId(null);
    if (selectedSymbolType === symbolType) {
      if (symbolPlacingMode === "single") {
        setSymbolPlacingMode("multiple");
      }
    } else {
      setSelectedSymbolType(symbolType);
      setSymbolPlacingMode("single");
    }
  };

  const handleClearSymbolSelection = () => {
    setSelectedSymbolType(null);
    setSymbolPlacingMode("single");
  };

  const handleCanvasClick = async (e) => {
    if (!selectedSymbolType) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    if (
      clickX < 0 ||
      clickX > canvasSize.width ||
      clickY < 0 ||
      clickY > canvasSize.height
    ) {
      return;
    }

    let positionMeters;

    if (layoutType === "plane") {
      // The symbol is centered on the click, so the click IS the true canvas
      // position. The old code anchored to the glyph's top-left (clickX -
      // SYMBOL_HALF_SIZE) but read it back through a +STATION_HALF_SIZE
      // inverse, storing the symbol 6px/scale meters up-left of the click; the
      // line/tunnel branches below never had that bug.
      positionMeters = viewport.unproject({ x: clickX, y: clickY });
    } else if (layoutType === "line") {
      positionMeters = viewports.line.pick({ x: clickX, y: clickY });
    } else if (layoutType === "tunnel") {
      const symbolType = getSymbolById(selectedSymbolType);
      positionMeters = viewports.tunnel.pick({ x: clickX, y: clickY });
      if (symbolType?.renderType === "vertical_line") {
        positionMeters = { x: positionMeters.x, y: dimensions.height / 2 };
      }
    } else {
      return;
    }

    const newSymbol = {
      symbol_type: selectedSymbolType,
      position_x_meters: positionMeters.x,
      position_y_meters: positionMeters.y,
      rotation: 0,
      scale: 1,
    };

    try {
      const response = await IndoorPositioningSymbols.CreateSymbol(
        machineID,
        newSymbol,
      );
      if (response.code === 200 && response.data) {
        const createdSymbol = {
          ...response.data,
          position_meters: positionMeters,
        };
        setPlacedSymbols((prev) => [...prev, createdSymbol]);
        message.success("已添加物件標記");

        if (symbolPlacingMode === "single") {
          setSelectedSymbolType(null);
          setSymbolPlacingMode("single");
        }
      } else {
        message.error(response.message || "添加物件標記失敗");
      }
    } catch (error) {
      console.error("添加物件標記失敗:", error);
      message.error("添加物件標記失敗");
    }
  };

  const handleSymbolDragStop = async (symbolId, data) => {
    let updatedSymbol = null;

    // Same half-glyph correction as handleDragStop, but with the SYMBOL glyph's
    // own half size - matching handleLineSymbolDragStop /
    // handleTunnelSymbolDragStop. The old projection added back only
    // STATION_HALF_SIZE here, storing every plane symbol 6px/scale meters
    // up-left of the drag; that is fixed, not preserved.
    const positionPixels = {
      x: data.x + SYMBOL_HALF_SIZE,
      y: data.y + SYMBOL_HALF_SIZE,
    };
    const positionMeters = viewport.unproject(positionPixels);

    setPlacedSymbols((prev) =>
      prev.map((symbol) => {
        if (symbol.id === symbolId) {
          updatedSymbol = {
            ...symbol,
            position_meters: positionMeters,
          };
          return updatedSymbol;
        }
        return symbol;
      }),
    );

    if (updatedSymbol) {
      try {
        await IndoorPositioningSymbols.UpdateSymbol(symbolId, {
          position_x_meters: positionMeters.x,
          position_y_meters: positionMeters.y,
        });
      } catch (error) {
        console.error("保存物件位置失敗:", error);
        message.error("保存物件位置失敗");
      }
    }
  };

  const handleLineSymbolDragStop = async (symbolId, data) => {
    let updatedSymbol = null;

    const position_meters = viewports.line.pick({
      x: data.x + SYMBOL_HALF_SIZE,
      y: data.y + SYMBOL_HALF_SIZE,
    });

    setPlacedSymbols((prev) =>
      prev.map((symbol) => {
        if (symbol.id === symbolId) {
          updatedSymbol = {
            ...symbol,
            position_meters,
          };
          return updatedSymbol;
        }
        return symbol;
      }),
    );

    if (updatedSymbol) {
      try {
        await IndoorPositioningSymbols.UpdateSymbol(symbolId, {
          position_x_meters: updatedSymbol.position_meters.x,
          position_y_meters: updatedSymbol.position_meters.y,
        });
      } catch (error) {
        console.error("保存物件位置失敗:", error);
        message.error("保存物件位置失敗");
      }
    }
  };

  const handleTunnelSymbolDragStop = async (symbolId, data) => {
    let updatedSymbol = null;

    const symbol = placedSymbols.find((s) => s.id === symbolId);
    const symbolType = getSymbolById(symbol?.symbol_type);

    if (symbolType?.renderType === "vertical_line") {
      const xMeters = viewports.tunnel.pick({ x: data.x + 2, y: 0 }).x;

      setPlacedSymbols((prev) =>
        prev.map((s) => {
          if (s.id === symbolId) {
            const position_meters = {
              x: xMeters,
              y: dimensions.height / 2,
            };
            updatedSymbol = {
              ...s,
              position_meters,
            };
            return updatedSymbol;
          }
          return s;
        }),
      );

      if (updatedSymbol) {
        try {
          await IndoorPositioningSymbols.UpdateSymbol(symbolId, {
            position_x_meters: updatedSymbol.position_meters.x,
            position_y_meters: updatedSymbol.position_meters.y,
          });
        } catch (error) {
          console.error("保存物件位置失敗:", error);
          message.error("保存物件位置失敗");
        }
      }
      return;
    }

    const position_meters = viewports.tunnel.pick({
      x: data.x + SYMBOL_HALF_SIZE,
      y: data.y + SYMBOL_HALF_SIZE,
    });

    setPlacedSymbols((prev) =>
      prev.map((symbol) => {
        if (symbol.id === symbolId) {
          updatedSymbol = {
            ...symbol,
            position_meters,
          };
          return updatedSymbol;
        }
        return symbol;
      }),
    );

    if (updatedSymbol) {
      try {
        await IndoorPositioningSymbols.UpdateSymbol(symbolId, {
          position_x_meters: updatedSymbol.position_meters.x,
          position_y_meters: updatedSymbol.position_meters.y,
        });
      } catch (error) {
        console.error("保存物件位置失敗:", error);
        message.error("保存物件位置失敗");
      }
    }
  };

  const handleDeleteAllSymbols = () => {
    if (placedSymbols.length === 0) return;

    Modal.confirm({
      title: `確定要刪除所有 ${placedSymbols.length} 個物件標記嗎？`,
      content: "此操作無法復原",
      okText: "確定",
      cancelText: "取消",
      okType: "danger",
      onOk: async () => {
        try {
          await Promise.all(
            placedSymbols.map((symbol) =>
              IndoorPositioningSymbols.DeleteSymbol(symbol.id),
            ),
          );
          setPlacedSymbols([]);
          message.success("已刪除所有物件標記");
        } catch (error) {
          console.error("移除物件標記失敗:", error);
          message.error("移除物件標記失敗");
        }
      },
    });
  };

  const handleDeleteSymbol = async (symbolId) => {
    try {
      await IndoorPositioningSymbols.DeleteSymbol(symbolId);
      setPlacedSymbols((prev) => prev.filter((s) => s.id !== symbolId));
      message.success("已移除物件");
    } catch (error) {
      console.error("移除物件失敗:", error);
      message.error("移除物件失敗");
    }
  };

  const handleDeleteSymbolsByCategory = async (category) => {
    const symbolsToDelete = placedSymbols.filter((symbol) => {
      const symbolType = getSymbolById(symbol.symbol_type);
      return symbolType?.category === category;
    });

    if (symbolsToDelete.length === 0) return;

    try {
      await Promise.all(
        symbolsToDelete.map((symbol) =>
          IndoorPositioningSymbols.DeleteSymbol(symbol.id),
        ),
      );
      const deletedIds = new Set(symbolsToDelete.map((s) => s.id));
      setPlacedSymbols((prev) => prev.filter((s) => !deletedIds.has(s.id)));
      message.success(`已刪除 ${symbolsToDelete.length} 個「${category}」物件`);
    } catch (error) {
      console.error("移除物件失敗:", error);
      message.error("移除物件失敗");
    }
  };

  const focusedTripwire =
    tripwiresState.tripwires.find((t) => t.id === focusedTripwireId) || null;

  // Where to anchor the level-1 popover: clear of BOTH endpoint handles
  // (popoverAnchorForLine), from the SAME projection the overlay itself uses
  // for the line's endpoints, so it stays glued to the line in every layout.
  const tripwirePopoverAnchor = (points, layoutViewport) => {
    const [p1px, p2px] = layoutViewport.projectPoints(points);
    return popoverAnchorForLine(p1px, p2px, canvasSize);
  };

  const shownLayers = {
    stationsVisible: overlays.visible.stations,
    symbolsVisible: overlays.visible.symbols,
    tripwiresVisible: overlays.visible.tripwires,
  };
  const layerToggles = (layout) =>
    editorLayerToggles({
      layoutType: layout,
      visible: overlays.visible,
      onToggle: overlays.toggle,
      placedStations,
      placedSymbols,
      areaVertices: areaPolygon.vertices,
      tripwires: tripwiresState.tripwires,
    });

  return (
    <>
      {tripwireMessageContextHolder}
      <Modal
        title="平面圖設定"
        open={open}
        onCancel={() => {
          setSymbolDrawerOpen(false);
          setSelectedSymbolType(null);
          setSymbolPlacingMode("single");
          onClose();
        }}
        onOk={handleSave}
        okText="保存"
        cancelText="取消"
        width={1100}
        okButtonProps={{ loading: saving }}
        confirmLoading={saving}
        keyboard={false}
      >
        <Spin spinning={loading}>
          <div className="p-3">
            <FloorPlanConfiguration
              layoutType={layoutType}
              setLayoutType={setLayoutType}
              dimensions={dimensions}
              setDimensions={setDimensions}
              imageUrl={imageUrl}
              uploading={uploading}
              handleUpload={handleUpload}
              handleRemoveImage={handleRemoveImage}
              geo={geoForm}
              onGeoChange={setGeoForm}
              geoTileLayers={floorPlan.geoTileLayers}
              stations={placedStations}
              savedDimensions={savedDimensions}
            />

            <Row gutter={16}>
              <Col span={7}>
                <StationList
                  stations={stations}
                  filteredStations={filteredStations()}
                  placedStations={placedStations}
                  stationsLoading={stationsLoading}
                  nameFilter={nameFilter}
                  onNameFilterChange={setNameFilter}
                  placementFilter={placementFilter}
                  onPlacementFilterChange={setPlacementFilter}
                  categoryFilter={categoryFilter}
                  onCategoryFilterChange={setCategoryFilter}
                  getUniqueCategories={getUniqueCategories}
                  isStationPlaced={isStationPlaced}
                  onStationClick={handleStationClick}
                  onRemoveStation={handleRemoveFromCanvas}
                  onUpdateRadius={handleUpdateRadius}
                  onUpdateAllowedPersonnel={handleUpdateAllowedPersonnel}
                  personnel={personnel}
                  selectedStationId={selectedStationId}
                  setSelectedStationId={(id) => {
                    overlays.show("stations");
                    setSelectedStationId(id);
                  }}
                  batchMode={batchMode}
                  onBatchModeToggle={handleBatchModeToggle}
                  selectedStationsOrder={selectedStationsOrder}
                  selectedUnplacedCount={getSelectedUnplaced().length}
                  selectedPlacedCount={getSelectedPlaced().length}
                  onSelectionChange={handleSelectionChange}
                  onSelectAll={handleSelectAll}
                  onDeselectAll={handleDeselectAll}
                  onBatchPlace={handleOpenBatchPlacement}
                  onOpenReorder={handleOpenReorder}
                  onBatchRemove={handleBatchRemove}
                />
              </Col>

              <Col span={17}>
                <Card
                  size="small"
                  title={
                    layoutType === "plane" ? (
                      <EditModeTitle editor={editor} />
                    ) : layoutType === "tunnel" ? (
                      "隧道佈局畫布"
                    ) : (
                      "直線佈局畫布"
                    )
                  }
                  extra={
                    <Space size="small">
                      {layoutType === "plane" && (
                        <Tooltip title="定義人員定位的可行區域，計算結果會被限制在此範圍內">
                          <Button
                            type={areaEditMode ? "primary" : "default"}
                            size="small"
                            icon={<RiShapeLine />}
                            onClick={toggleAreaEditMode}
                          >
                            可行區域
                          </Button>
                        </Tooltip>
                      )}
                      <Tooltip
                        title={
                          tripwireAddMode.active
                            ? "取消新增越線"
                            : layoutType === "plane"
                              ? "點擊畫布兩點建立越線警報"
                              : "點擊車道上一點，建立橫跨車道的越線警報"
                        }
                      >
                        <Button
                          type={tripwireAddMode.active ? "primary" : "default"}
                          size="small"
                          icon={<RiAlarmWarningLine />}
                          onClick={toggleTripwireAddMode}
                        >
                          {tripwireAddMode.active
                            ? "取消新增"
                            : `越線${
                                tripwiresState.tripwires.length > 0
                                  ? ` (${tripwiresState.tripwires.length})`
                                  : ""
                              }`}
                        </Button>
                      </Tooltip>
                      <Button
                        type="default"
                        size="small"
                        icon={<RiShapesLine />}
                        onClick={() => setSymbolDrawerOpen(true)}
                      >
                        物件標記{" "}
                        {placedSymbols.length > 0 &&
                          `(${placedSymbols.length})`}
                      </Button>
                    </Space>
                  }
                  bordered
                  bodyStyle={{
                    padding: 12,
                    position: "relative",
                    height: CANVAS_CARD_BODY_HEIGHT,
                    overflow: "hidden",
                  }}
                >
                  <div
                    ref={canvasContainerRef}
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                      overflow: "visible",
                    }}
                  >
                    {tripwireAddMode.active && (
                      <div
                        style={{
                          position: "absolute",
                          top: 4,
                          left: "50%",
                          transform: "translateX(-50%)",
                          zIndex: 20,
                          background: "rgba(0, 0, 0, 0.75)",
                          color: "#fff",
                          fontSize: 12,
                          padding: "4px 12px",
                          borderRadius: 4,
                          pointerEvents: "none",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {getTripwireAddHint(
                          layoutType,
                          tripwireAddMode.points.length,
                        )}
                      </div>
                    )}
                    {/* 可行區域 editing is 編輯模式, where the zoom pill is
                        hidden, so the bar takes the pill's top-left slot:
                        the canvas's 40px y-axis gutter + 1px border + 8px. */}
                    {layoutType === "plane" && areaEditMode && (
                      <div
                        style={{
                          position: "absolute",
                          top: 9,
                          left: 49,
                          zIndex: 10,
                          display: "flex",
                          gap: 4,
                          alignItems: "center",
                          background: "rgba(255, 255, 255, 0.92)",
                          padding: "2px 6px",
                          borderRadius: 4,
                          boxShadow: "0 1px 4px rgba(0, 0, 0, 0.15)",
                        }}
                      >
                        <span style={{ fontSize: 12, color: "#555" }}>
                          {areaPolygon.vertices.length} 點
                          {areaPolygon.drawing
                            ? "（繪製中，點擊畫布加點）"
                            : ""}
                        </span>
                        {areaPolygon.drawing ? (
                          <>
                            <Tooltip title="復原上一點">
                              <Button
                                size="small"
                                icon={<RiArrowGoBackLine />}
                                onClick={areaPolygon.undo}
                                disabled={areaPolygon.vertices.length === 0}
                              />
                            </Tooltip>
                            <Button
                              size="small"
                              type="primary"
                              onClick={areaPolygon.finish}
                              disabled={areaPolygon.vertices.length < 3}
                            >
                              封閉
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="small"
                            onClick={() => areaPolygon.setDrawing(true)}
                          >
                            續繪
                          </Button>
                        )}
                        <Tooltip title="清除可行區域">
                          <Button
                            size="small"
                            danger
                            icon={<RiCloseLine />}
                            onClick={handleClearArea}
                            disabled={areaPolygon.vertices.length === 0}
                          />
                        </Tooltip>
                        <Tooltip title="完成並收合">
                          <Button
                            size="small"
                            type="primary"
                            icon={<RiCheckLine />}
                            onClick={handleFinishAreaEdit}
                          />
                        </Tooltip>
                      </div>
                    )}
                    <Tabs
                      activeKey={layoutType}
                      animated={{ tabPane: true }}
                      tabBarStyle={{ display: "none" }}
                      items={[
                        {
                          key: "plane",
                          label: "平面 (2D)",
                          children: (
                            <PlaneCanvas
                              dimensions={dimensions}
                              canvasSize={canvasSize}
                              canvasRef={canvasRef}
                              imageUrl={imageUrl}
                              placedStations={placedStations}
                              {...shownLayers}
                              selectedStationId={selectedStationId}
                              setSelectedStationId={setSelectedStationId}
                              handleDragStop={handleDragStop}
                              activeLayoutType={layoutType}
                              viewport={viewports.plane}
                              placedSymbols={placedSymbols}
                              handleSymbolDragStop={handleSymbolDragStop}
                              onCanvasClick={
                                tripwireAddMode.active
                                  ? handleTripwireCanvasClick
                                  : areaEditMode && areaPolygon.drawing
                                    ? handleAreaCanvasClick
                                    : selectedSymbolType
                                      ? handleCanvasClick
                                      : null
                              }
                              onCanvasMouseMove={
                                tripwireAddMode.active
                                  ? handleTripwireCanvasMouseMove
                                  : undefined
                              }
                              onDeleteSymbol={handleDeleteSymbol}
                              areaEditMode={areaEditMode}
                              areaVisible={overlays.visible.area}
                              layerToggles={layerToggles("plane")}
                              boundaryVertices={areaPolygon.vertices}
                              boundaryDrawing={areaPolygon.drawing}
                              onMoveVertex={areaPolygon.moveVertex}
                              onRemoveVertex={areaPolygon.removeVertex}
                              onVertexClick={handleVertexClick}
                              editor={editor}
                              tripwireLayer={
                                <>
                                  <TripwireOverlay
                                    layoutType="plane"
                                    tripwires={tripwiresState.tripwires}
                                    canvasSize={canvasSize}
                                    dimensions={dimensions}
                                    viewport={viewports.plane}
                                    focusedId={focusedTripwireId}
                                    onSelect={handleTripwireFocus}
                                    onBackgroundClick={
                                      handleTripwireBackgroundClick
                                    }
                                    onEndpointDragEnd={
                                      handleTripwireEndpointDragEnd
                                    }
                                    onBodyDragEnd={handleTripwireBodyDragEnd}
                                    drawingPoints={tripwireAddMode.points}
                                    previewPoint={tripwireAddMode.previewPoint}
                                    onDragActiveChange={setTripwireDragActive}
                                    editable={editor.editing}
                                  />
                                  {focusedTripwire &&
                                    layoutType === "plane" &&
                                    !tripwireModalOpen &&
                                    !tripwireDragActive &&
                                    (() => {
                                      const anchor = tripwirePopoverAnchor(
                                        focusedTripwire.points,
                                        viewports.plane,
                                      );
                                      return (
                                        <TripwirePopover
                                          tripwire={focusedTripwire}
                                          anchorPosition={anchor}
                                          placement={anchor.placement}
                                          onEdit={handleTripwireEditOpen}
                                          onFlip={() =>
                                            handleTripwireFlip(
                                              focusedTripwire.id,
                                              focusedTripwire.points,
                                            )
                                          }
                                          onDelete={() =>
                                            handleTripwireDelete(
                                              focusedTripwire.id,
                                            )
                                          }
                                        />
                                      );
                                    })()}
                                </>
                              }
                            />
                          ),
                        },
                        {
                          key: "line",
                          label: "直線 (1D)",
                          children: (
                            <LineCanvas
                              dimensions={dimensions}
                              canvasSize={canvasSize}
                              canvasRef={canvasRef}
                              placedStations={placedStations}
                              {...shownLayers}
                              selectedStationId={selectedStationId}
                              setSelectedStationId={setSelectedStationId}
                              handleLineDragStop={handleLineDragStop}
                              activeLayoutType={layoutType}
                              placedSymbols={placedSymbols}
                              handleSymbolDragStop={handleLineSymbolDragStop}
                              onCanvasClick={
                                tripwireAddMode.active
                                  ? handleTripwireCanvasClick
                                  : selectedSymbolType
                                    ? handleCanvasClick
                                    : null
                              }
                              onDeleteSymbol={handleDeleteSymbol}
                              layerToggles={layerToggles("line")}
                              tripwireLayer={
                                <>
                                  <TripwireOverlay
                                    layoutType="line"
                                    viewport={viewports.line}
                                    tripwires={tripwiresState.tripwires}
                                    canvasSize={canvasSize}
                                    dimensions={dimensions}
                                    focusedId={focusedTripwireId}
                                    onSelect={handleTripwireFocus}
                                    onBackgroundClick={
                                      handleTripwireBackgroundClick
                                    }
                                    onBodyDragEnd={handleTripwireBodyDragEnd}
                                    onLaneDragEnd={handleTripwireLaneDragEnd}
                                    onDragActiveChange={setTripwireDragActive}
                                  />
                                  {focusedTripwire &&
                                    layoutType === "line" &&
                                    !tripwireModalOpen &&
                                    !tripwireDragActive &&
                                    (() => {
                                      const anchor = tripwirePopoverAnchor(
                                        focusedTripwire.points,
                                        viewports.line,
                                      );
                                      return (
                                        <TripwirePopover
                                          tripwire={focusedTripwire}
                                          anchorPosition={anchor}
                                          placement={anchor.placement}
                                          onEdit={handleTripwireEditOpen}
                                          onFlip={() =>
                                            handleTripwireFlip(
                                              focusedTripwire.id,
                                              focusedTripwire.points,
                                            )
                                          }
                                          onDelete={() =>
                                            handleTripwireDelete(
                                              focusedTripwire.id,
                                            )
                                          }
                                        />
                                      );
                                    })()}
                                </>
                              }
                            />
                          ),
                        },
                        {
                          key: "tunnel",
                          label: "隧道 (雙線)",
                          children: (
                            <TunnelCanvas
                              dimensions={dimensions}
                              canvasSize={canvasSize}
                              viewport={viewports.tunnel}
                              canvasRef={canvasRef}
                              placedStations={placedStations}
                              {...shownLayers}
                              selectedStationId={selectedStationId}
                              setSelectedStationId={setSelectedStationId}
                              handleTunnelDragStop={handleTunnelDragStop}
                              activeLayoutType={layoutType}
                              placedSymbols={placedSymbols}
                              handleSymbolDragStop={handleTunnelSymbolDragStop}
                              onCanvasClick={
                                tripwireAddMode.active
                                  ? handleTripwireCanvasClick
                                  : selectedSymbolType
                                    ? handleCanvasClick
                                    : null
                              }
                              onDeleteSymbol={handleDeleteSymbol}
                              layerToggles={layerToggles("tunnel")}
                              tripwireLayer={
                                <>
                                  <TripwireOverlay
                                    layoutType="tunnel"
                                    viewport={viewports.tunnel}
                                    tripwires={tripwiresState.tripwires}
                                    canvasSize={canvasSize}
                                    dimensions={dimensions}
                                    focusedId={focusedTripwireId}
                                    onSelect={handleTripwireFocus}
                                    onBackgroundClick={
                                      handleTripwireBackgroundClick
                                    }
                                    onBodyDragEnd={handleTripwireBodyDragEnd}
                                    onLaneDragEnd={handleTripwireLaneDragEnd}
                                    onDragActiveChange={setTripwireDragActive}
                                  />
                                  {focusedTripwire &&
                                    layoutType === "tunnel" &&
                                    !tripwireModalOpen &&
                                    !tripwireDragActive &&
                                    (() => {
                                      const anchor = tripwirePopoverAnchor(
                                        focusedTripwire.points,
                                        viewports.tunnel,
                                      );
                                      return (
                                        <TripwirePopover
                                          tripwire={focusedTripwire}
                                          anchorPosition={anchor}
                                          placement={anchor.placement}
                                          onEdit={handleTripwireEditOpen}
                                          onFlip={() =>
                                            handleTripwireFlip(
                                              focusedTripwire.id,
                                              focusedTripwire.points,
                                            )
                                          }
                                          onDelete={() =>
                                            handleTripwireDelete(
                                              focusedTripwire.id,
                                            )
                                          }
                                        />
                                      );
                                    })()}
                                </>
                              }
                            />
                          ),
                        },
                      ]}
                    />
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        </Spin>
        <BatchPlacementModal
          open={batchPlacementModalOpen}
          onClose={() => setBatchPlacementModalOpen(false)}
          onConfirm={handleBatchPlacement}
          selectedCount={getSelectedUnplaced().length}
          layoutType={layoutType}
          dimensions={dimensions}
        />
        <ReorderModal
          open={reorderModalOpen}
          onClose={() => setReorderModalOpen(false)}
          stations={getSelectedUnplaced()}
          onReorder={handleReorder}
        />
        <TripwireSettingsModal
          open={tripwireModalOpen}
          tripwire={tripwireDraft}
          machineID={machineID}
          personnel={personnel}
          dimensions={dimensions}
          layoutType={layoutType}
          boundaryArea={areaPolygon.vertices}
          onClose={closeTripwireModal}
          onSave={handleTripwireSave}
          onDelete={handleTripwireDelete}
          onFlip={handleTripwireFlip}
          saving={tripwiresState.saving}
          messageApi={tripwireMessage}
        />
      </Modal>
      <Drawer
        title="物件標記"
        placement="right"
        width={320}
        onClose={() => setSymbolDrawerOpen(false)}
        open={open && symbolDrawerOpen}
        keyboard={false}
        mask={false}
        bodyStyle={{ padding: 16 }}
      >
        <SymbolPalette
          layoutType={layoutType}
          selectedSymbolType={selectedSymbolType}
          symbolPlacingMode={symbolPlacingMode}
          onSymbolSelect={handleSymbolSelect}
          onClearSelection={handleClearSymbolSelection}
          placedSymbolsCount={placedSymbols.length}
          placedSymbols={placedSymbols}
          onDeleteAll={handleDeleteAllSymbols}
          onDeleteByCategory={handleDeleteSymbolsByCategory}
        />
      </Drawer>
    </>
  );
};

export default FloorPlanModal;
